import { useEffect, useMemo, useState } from "react"
import { bool } from 'prop-types';
import { useParams, useLocation } from "react-router-dom"

import { Accordion, CheckboxButton, FormLabel, InputLista, InputNumber, Loading, SectionHeading, Select, FormError } from "../../components/common"
import { useForm, useNav, useUnsavedChangesGuard, useFormErrors, useManagedContext } from "../../components/hooks"
import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage, TRIBUTO_TYPE } from '@laramie/ui';
import { ServerRequest } from "../../utils/apiweb"
import { APIS } from "../../config/apis"

import { VALIDATION_MESSAGE } from "../../consts/validationMessage"
import Conceptos from "./components/Conceptos"
import { isValidInteger, isValidString } from "../../utils/validator";
import { FooterButtonVolver } from "../../components/buttons/FooterButtonVolver/FooterButtonVolver"


const ReciboEspecialView = (props) => {
    const params = useParams()
    const location = useLocation()
    const previousFilters = location.state?.filters
    const navigate = useNav()
    const { showMessageModal } = useManagedContext()

    const [conceptos, setConceptos] = useState([])
    const [pendingChange, setPendingChange] = useState(false)
    const [loading, setLoading] = useState(false)
    const [originalDerechoEspecial, setOriginalDerechoEspecial] = useState(null)

    const filteredConceptos = useMemo(() => conceptos.filter(x => x.state !== 'r'), [conceptos])

    const [formValues, formHandle, , setFormValues] = useForm({
        idTipoTributo: props.reciboEspecial ? TRIBUTO_TYPE.CUENTAS_ESPECIALES : 0,
        derechoEspecial: !props.reciboEspecial,
        codigo: '',
        descripcion: '',
        aplicaValorUF: false,
        activo: false
    })
    const formHandleProxy = (event) => {
        formHandle(event)
        setPendingChange(true)
    }

    const {allowNavigation} = useUnsavedChangesGuard(pendingChange)

    useEffect(() => {
        if (params.mode !== OPERATION_MODE.NEW) {
            findRecibo()
        }
    }, [])


    const onNoSuccess = (response) => {
        response.json() 
        .then((error) => { ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error.message) })
        .catch((error) => { ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error) })
        .finally(() => setLoading(false))
    }

    const onError = (error) => {
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando solicitud: ' + error.message);
        setLoading(false)
    }

    const findRecibo = () => {

        setLoading(true)

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.RECIBO_ESPECIAL,
            `/${params.id}`,
            null,
            res => {
                res.json()
                    .then(data => {
                        data.recibosEspecialConcepto.forEach(row => {
                            if (row.fechaDesde) row.fechaDesde = new Date(row.fechaDesde);
                            if (row.fechaHasta) row.fechaHasta = new Date(row.fechaHasta);
                        });
                        setFormValues(data.reciboEspecial)
                        setConceptos(data.recibosEspecialConcepto)
                        setOriginalDerechoEspecial(data.reciboEspecial.derechoEspecial)
                    })
                    .catch(onNoSuccess)
                    .finally(() => setLoading(false))
            },
            onNoSuccess,
            onError,
        )
    }

    const addRecibo = () => {
        if (!validate()) return;
        setLoading(true)
        ServerRequest(
            REQUEST_METHOD.POST,
            null,
            true,
            APIS.URLS.RECIBO_ESPECIAL,
            null,
            {
                reciboEspecial: {...formValues, id: -new Date()},
                recibosEspecialConcepto: [],
            },
            res => {
                res.json()
                    .then(data => {
                        ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, formValues.derechoEspecial ? 'Derecho especial generado exitosamente' : 'VEP especial generado exitosamente')
                        allowNavigation()
                        navigate({
                            to: (formValues.derechoEspecial ? '/derecho-especial/' : '/recibo-especial/') + `edit/${data.reciboEspecial.id}`,
                            refresh: true,
                            replace: true,
                        })
                    })
                    .catch(onNoSuccess)
                    .finally(() => setLoading(false))
            },
            onNoSuccess,
            onError,
        )
    }

    const modifyRecibo = () => {
        if (!validate()) return;

        if (originalDerechoEspecial !== null && formValues.derechoEspecial !== originalDerechoEspecial) {
            showMessageModal({
                title: 'Confirmación',
                message: `La definición va a pasar a listarse en ${formValues.derechoEspecial ? 'Definición de derechos especiales' : 'Definición de VEPs especiales'} y va a cambiar el momento en que se genera la deuda (${formValues.derechoEspecial ? 'al emitir' : 'al pagar'}). ¿Desea continuar?`,
                onConfirm: () => executeModifyRecibo(),
            })
            return
        }

        executeModifyRecibo()
    }

    const executeModifyRecibo = () => {
        setLoading(true)
        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.RECIBO_ESPECIAL,
            `/${params.id}`,
            {
                reciboEspecial: formValues,
                recibosEspecialConcepto: conceptos,
            },
            res => {
                // setLoading(false)
                setPendingChange(false)
                // setOriginalDerechoEspecial(formValues.derechoEspecial)
                findRecibo();
                ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, formValues.derechoEspecial ? 'Derecho especial modificado exitosamente' : 'VEP especial modificado exitosamente')
            },
            onNoSuccess,
            onError,
        )
    }


    const onAddConcepto = (item) => {
        setConceptos(prev => [...prev, item])
        setPendingChange(true)
    }

    const onModifyConcepto = (item) => {
        setConceptos(prev => prev.map(x => x.id === item.id ? item : x))
        setPendingChange(true)
    }

    const onRemoveConcepto = (item) => {
        const newConceptos = []
        conceptos.forEach(concepto => {
            if (concepto.id === item.id) {
                if (concepto.state !== 'a')
                    newConceptos.push({...concepto, state: 'r'})
            }
            else newConceptos.push(concepto)
        })
        setConceptos(newConceptos)
        setPendingChange(true)
    }

    const getErrors = () => {

        const errors = {};

        if (!isValidInteger(formValues.idTipoTributo,true)) errors.idTipoTributo = VALIDATION_MESSAGE.REQUIRED_OPTION;
        if (!isValidString(formValues.codigo,true)) errors.codigo = VALIDATION_MESSAGE.REQUIRED;
        if (!isValidString(formValues.descripcion,true)) errors.descripcion = VALIDATION_MESSAGE.REQUIRED;

        return errors;
    }

    const { errorOf, validate } = useFormErrors(getErrors);

    return (
        <div>
            <Loading visible={loading} />

            <SectionHeading
                titles={[
                    { title: 'Facturación', },
                    {
                        title: props.reciboEspecial ? 'Definición de VEP especial' : 'Definición de derecho especial',
                        url: props.reciboEspecial ? '/recibos-especiales' : '/derechos-especiales'
                    },
                    { title: params.mode === OPERATION_MODE.NEW ? 'Nueva' : `Código: ${formValues.codigo}`}
                ]}
            />

            <section className="section-accordion">
                <div className="m-top-20 m-bottom-20">
                    <Accordion title="Definición" startOpen>
                        <div className='row form-basic'>
                            <div className="col-12 col-md-2">
                                <FormLabel htmlFor="idPersona" required>Tipo de tributo</FormLabel>
                                <InputLista
                                    name="idTipoTributo"
                                    placeholder=""
                                    className={`form-control ${errorOf('idTipoTributo') ? 'is-invalid' : ''}`}
                                    value={formValues.idTipoTributo}
                                    onChange={formHandleProxy}
                                    lista="TipoTributo"
                                    disabled={params.mode === OPERATION_MODE.VIEW || formValues.derechoEspecial === false}
                                    filter={(row) => formValues.derechoEspecial === true || row.id === TRIBUTO_TYPE.CUENTAS_ESPECIALES}
                                />
                                <FormError message={errorOf('idTipoTributo')} />
                            </div>
                            <div className="col-12 col-md-2">
                                <FormLabel htmlFor="derechoEspecial">Tipo de emisión</FormLabel>
                                <Select
                                    name="derechoEspecial"
                                    className="form-control"
                                    value={formValues.derechoEspecial}
                                    options={[
                                        { value: false, label: 'VEP especial' },
                                        { value: true, label: 'Derecho especial' }
                                    ]}
                                    onChange={(event) => {
                                        formHandleProxy(event)
                                        if (event.target.value === false) {
                                            formHandleProxy({ target: { name: 'idTipoTributo', type: 'lista', value: TRIBUTO_TYPE.CUENTAS_ESPECIALES } })
                                        }
                                    }}
                                    disabled={params.mode === OPERATION_MODE.VIEW}
                                />
                            </div>
                            <div className="col-12 col-md-2">
                                <FormLabel htmlFor="codigo" required>Código</FormLabel>
                                <input
                                    name="codigo"
                                    type="text"
                                    placeholder=""
                                    className={`form-control ${errorOf('codigo') ? 'is-invalid' : ''}`}
                                    value={formValues.codigo}
                                    onChange={formHandleProxy}
                                    disabled={params.mode === OPERATION_MODE.VIEW}
                                />
                                <FormError message={errorOf('codigo')} />
                            </div>
                            <div className="col-12 col-md-6">
                                <FormLabel htmlFor="descripcion" required>Descripción</FormLabel>
                                <input
                                    name="descripcion"
                                    type="text"
                                    placeholder=""
                                    className={`form-control ${errorOf('descripcion') ? 'is-invalid' : ''}`}
                                    value={formValues.descripcion}
                                    onChange={formHandleProxy}
                                    disabled={params.mode === OPERATION_MODE.VIEW}
                                />
                                <FormError message={errorOf('descripcion')} />
                            </div>
                            <div className="col-12 col-md-auto align-self-end">
                                <CheckboxButton
                                    name="aplicaValorUF"
                                    label="Aplica valor UF"
                                    checked={formValues.aplicaValorUF}
                                    onChange={formHandleProxy}
                                    disabled={params.mode === OPERATION_MODE.VIEW}
                                />
                            </div>
                            <div className="col-12 col-md-auto align-self-end">
                                <CheckboxButton
                                    name="activo"
                                    label="Activo"
                                    checked={formValues.activo}
                                    onChange={formHandleProxy}
                                    disabled={params.mode === OPERATION_MODE.VIEW}
                                />
                            </div>
                        </div>
                    </Accordion>

                    {params.mode !== OPERATION_MODE.NEW &&
                    <div className="m-top-20 m-bottom-20">
                    <Conceptos idTipoTributo={formValues.idTipoTributo??0} aplicaValorUF={formValues.aplicaValorUF} {...{ params, onAddConcepto, onModifyConcepto, onRemoveConcepto, conceptos: filteredConceptos }} />
                    </div>
                    }
                </div>
            </section>

            <footer className='footer footer-action'>
                <div className='footer-action-container'>
                    <FooterButtonVolver
                        pendingChange={pendingChange}
                        filters={previousFilters}
                        to={props.reciboEspecial ? '/recibos-especiales' : '/derechos-especiales'}
                        allowNavigation={allowNavigation}
                    />
                    {params.mode !== OPERATION_MODE.VIEW && (
                        <button
                            className={pendingChange ? "btn action-button float-end" : "btn action-button btn-disabled float-end"}
                            onClick={params.mode === OPERATION_MODE.NEW ? addRecibo : modifyRecibo}
                            disabled={!pendingChange}
                        >
                            {params.mode === OPERATION_MODE.NEW ? 'Siguiente' : 'Guardar'}
                        </button>
                    )}
                </div>
            </footer>
        </div>
    )
}

ReciboEspecialView.propTypes = {
    reciboEspecial: bool.isRequired
};

export default ReciboEspecialView
